import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

const DRAWER_TRANSITION_MS = 300;
const TRANSITION_CLASS = "transition-all duration-slow ease-standard";
const DRAWER_TRANSITION_STYLE = {
  transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;
const PANEL_BASE_CLASS =
  "fixed z-[45] border-border-subtle bg-surface-primary text-content-primary shadow-floating will-change-transform";
const DRAWER_STACK_CLASS = "fixed inset-0 z-[60] isolate";
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

type DrawerSide = "left" | "right";
type AnimationState = "idle" | "entering" | "visible" | "exiting";

export interface DrawerShellProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  side?: DrawerSide;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  panelId?: string;
  topOffset?: boolean;
  panelWidthClassName?: string;
  compensateViewportScrollbar?: boolean;
  overlayClassName?: string;
  panelClassName?: string;
  topOffsetClassName?: string;
  overlayTopOffsetClassName?: string;
}

let bodyScrollLockCount = 0;
let previousBodyOverflow = "";
const openDrawerStack: string[] = [];

function getFocusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => {
      const isHidden =
        element.hidden ||
        element.getAttribute("aria-hidden") === "true" ||
        element.offsetParent === null;

      return !isHidden;
    });
}

function focusDrawerElement(panel: HTMLElement, position: "first" | "last" = "first") {
  const focusableElements = getFocusableElements(panel);
  const target =
    position === "last"
      ? focusableElements[focusableElements.length - 1]
      : focusableElements[0];

  (target ?? panel).focus();
}

export const DrawerShell = ({
  isOpen,
  onClose,
  children,
  side = "right",
  ariaLabel,
  ariaLabelledBy,
  panelId,
  topOffset = false,
  panelWidthClassName = "w-full max-w-xl",
  compensateViewportScrollbar = true,
  overlayClassName = "bg-surface-accent/45",
  panelClassName,
  topOffsetClassName = "top-[calc(4rem+1px)] h-[calc(100dvh-4rem-1px)]",
  overlayTopOffsetClassName = "inset-x-0 bottom-0 top-[calc(4rem+1px)]",
}: DrawerShellProps) => {
  const drawerId = useId();
  const [animationState, setAnimationState] = useState<AnimationState>("idle");
  const animationStateRef = useRef<AnimationState>("idle");
  const timeoutRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);
  const entryFrameRef = useRef<number | null>(null);
  const [isAnimated, setIsAnimated] = useState(false);
  const panelRef = useRef<HTMLElement | null>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  const isTopMostDrawer = useCallback(() => {
    return openDrawerStack[openDrawerStack.length - 1] === drawerId;
  }, [drawerId]);

  const setPhase = useCallback((state: AnimationState) => {
    animationStateRef.current = state;
    setAnimationState(state);
  }, []);

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current && typeof window !== "undefined") {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const cancelFrame = useCallback(() => {
    if (frameRef.current && typeof window !== "undefined") {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      setPhase(isOpen ? "visible" : "idle");
      return;
    }

    if (isOpen) {
      clearTimeoutRef();
      if (
        animationStateRef.current === "visible" ||
        animationStateRef.current === "entering"
      ) {
        return;
      }

      setPhase("entering");
      cancelFrame();
      frameRef.current = window.requestAnimationFrame(() => {
        setPhase("visible");
        frameRef.current = null;
      });
      return;
    }

    if (
      animationStateRef.current === "idle" ||
      animationStateRef.current === "exiting"
    ) {
      return;
    }

    cancelFrame();
    setPhase("exiting");
    clearTimeoutRef();
    timeoutRef.current = window.setTimeout(() => {
      setPhase("idle");
      timeoutRef.current = null;
    }, DRAWER_TRANSITION_MS);
  }, [cancelFrame, clearTimeoutRef, isOpen, setPhase]);

  useEffect(() => {
    return () => {
      clearTimeoutRef();
      cancelFrame();
      if (entryFrameRef.current && typeof window !== "undefined") {
        window.cancelAnimationFrame(entryFrameRef.current);
        entryFrameRef.current = null;
      }
    };
  }, [cancelFrame, clearTimeoutRef]);

  useEffect(() => {
    const isRendered = animationState !== "idle";

    if (!isRendered) {
      setIsAnimated(false);
      return;
    }

    if (typeof window === "undefined") {
      setIsAnimated(isOpen);
      return;
    }

    if (!isOpen) {
      setIsAnimated(false);
      return;
    }

    setIsAnimated(false);
    entryFrameRef.current = window.requestAnimationFrame(() => {
      setIsAnimated(true);
    });

    return () => {
      if (entryFrameRef.current && typeof window !== "undefined") {
        window.cancelAnimationFrame(entryFrameRef.current);
        entryFrameRef.current = null;
      }
    };
  }, [animationState, isOpen]);

  const handleOverlayClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (isTopMostDrawer()) {
        onClose();
      }
    },
    [isTopMostDrawer, onClose],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    openDrawerStack.push(drawerId);
    return () => {
      const index = openDrawerStack.lastIndexOf(drawerId);
      if (index >= 0) {
        openDrawerStack.splice(index, 1);
      }
    };
  }, [drawerId, isOpen]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return;
    }

    const { body, documentElement } = document;
    if (bodyScrollLockCount === 0) {
      previousBodyOverflow = body.style.overflow;
      body.style.overflow = "hidden";
      if (compensateViewportScrollbar) {
        const viewportScrollbarWidth =
          window.innerWidth - documentElement.clientWidth;
        if (viewportScrollbarWidth > 0) {
          body.style.paddingRight = `${viewportScrollbarWidth}px`;
        }
      }
    }
    bodyScrollLockCount += 1;

    return () => {
      bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
      if (bodyScrollLockCount === 0) {
        body.style.overflow = previousBodyOverflow;
        body.style.paddingRight = "";
      }
    };
  }, [compensateViewportScrollbar, isOpen]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return;
    }

    previouslyFocusedElementRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    return () => {
      const previouslyFocusedElement = previouslyFocusedElementRef.current;
      previouslyFocusedElementRef.current = null;
      if (previouslyFocusedElement?.isConnected) {
        previouslyFocusedElement.focus();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopMostDrawer()) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const panel = panelRef.current;
      if (!panel) {
        return;
      }

      const focusableElements = getFocusableElements(panel);
      if (focusableElements.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;
      const isFocusInsidePanel =
        activeElement instanceof Node && panel.contains(activeElement);

      if (!isFocusInsidePanel) {
        event.preventDefault();
        focusDrawerElement(panel, event.shiftKey ? "last" : "first");
        return;
      }

      if (event.shiftKey) {
        if (activeElement === firstFocusableElement || activeElement === panel) {
          event.preventDefault();
          lastFocusableElement.focus();
        }
        return;
      }

      if (activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isTopMostDrawer, onClose]);

  useEffect(() => {
    if (
      animationState !== "visible" ||
      !isTopMostDrawer() ||
      typeof document === "undefined"
    ) {
      return;
    }

    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    focusDrawerElement(panel);
  }, [animationState, isTopMostDrawer]);

  useEffect(() => {
    if (!isOpen || animationState !== "visible" || typeof document === "undefined") {
      return;
    }

    const handleFocusIn = (event: FocusEvent) => {
      if (!isTopMostDrawer()) {
        return;
      }

      const panel = panelRef.current;
      const target = event.target;
      if (!panel || !(target instanceof Node) || panel.contains(target)) {
        return;
      }

      focusDrawerElement(panel);
    };

    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, [animationState, isOpen, isTopMostDrawer]);

  if (animationState === "idle" || typeof document === "undefined") {
    return null;
  }

  const visible = animationState === "visible";
  const sidePositionClass = side === "left" ? "left-0 border-r" : "right-0 border-l";
  const horizontalTransformClass =
    side === "left"
      ? visible && isAnimated
        ? "translate-x-0"
        : "-translate-x-full"
      : visible && isAnimated
        ? "translate-x-0"
        : "translate-x-full";
  const panelOffsetClass = topOffset ? topOffsetClassName : "top-0 h-dvh";
  const overlayOffsetClass = topOffset ? overlayTopOffsetClassName : "inset-0";

  return createPortal(
    <div className={DRAWER_STACK_CLASS} aria-hidden={isOpen ? undefined : true}>
      <div
        className={clsx(
          "fixed z-40",
          overlayClassName,
          overlayOffsetClass,
          TRANSITION_CLASS,
          visible && isAnimated ? "opacity-100" : "opacity-0",
        )}
        style={DRAWER_TRANSITION_STYLE}
        onClick={handleOverlayClick}
      />
      <aside
        ref={panelRef}
        id={panelId}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        className={clsx(
          PANEL_BASE_CLASS,
          sidePositionClass,
          panelOffsetClass,
          panelWidthClassName,
          TRANSITION_CLASS,
          horizontalTransformClass,
          panelClassName,
        )}
        style={DRAWER_TRANSITION_STYLE}
      >
        {children}
      </aside>
    </div>,
    document.body,
  );
};

export default DrawerShell;
